// how-to-start/store/deck-build.store.ts
//
// THE DECK THAT IS STILL BEING BUILT, held somewhere the editor can see it.
//
// The funnel used to generate every slide behind a loader and only then create
// the story, so the user watched a progress bar for as long as the deck took.
// Now the funnel generates ONE slide, creates the story with it and opens the
// editor; the rest arrive in the editor, in order, while the user watches the
// real document fill.
//
// That crosses a navigation, which is the whole reason this store exists. The
// job cannot live in the funnel's component tree -- it unmounts -- and it
// cannot live in the editor's either, because the funnel starts it before the
// editor exists. It lives here, between them: ../deck-build-runner (mounted in
// the app root, above the router) does the work, and both the funnel loader
// and the editor's banner read the progress.

import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { DeckInputs } from "../hooks/use-funnel-generation";

/** What the runner needs to finish a deck it did not start. */
export type DeckBuildJob = {
  /**
   * The story the funnel created, holding one blank placeholder slide.
   *
   * It is created before anything has been generated -- that is what lets the
   * editor open immediately -- so the runner owns EVERY slide, the cover
   * included, and drops the placeholder once it has a real one to show.
   */
  storyId: string;
  /**
   * The standalone version the deck is built into, when it is one: the
   * editor then has the story's HOST open, with this version on it, and the
   * runner checks for both. Null for a story of the user's own.
   */
  versionId?: string | null;
  /** What to build. Planned by the runner, not before it. */
  inputs: DeckInputs;
  /**
   * The brand whose logo replaces template placeholders.
   *
   * Slide one gets this during story creation (see
   * ../hooks/use-complete-funnel); slides that arrive later have to be given
   * it here, or a deck would open with the user's own logo on the cover and
   * "Logoipsum" on everything after it.
   */
  brandImport: unknown;
  /**
   * The title the story was created under -- its document type, since the
   * deck's own title is not known until it is planned. The runner replaces
   * it with the deck's title, but only while it still reads this: a name the
   * user has already typed over is theirs.
   */
  placeholderTitle: string;
};

export type DeckBuildStatus =
  /** No deck is being built. */
  | "idle"
  /** Working out what the deck is; no slide count yet. */
  | "planning"
  /** Slides are still arriving. */
  | "building"
  /** Every slide arrived. */
  | "done"
  /** The user pressed Stop; what is in the deck stays. */
  | "stopped"
  /** The run finished but some slides never generated; they can be retried. */
  | "partial";

type DeckBuildState = {
  job: DeckBuildJob | null;
  status: DeckBuildStatus;
  /** Slides the finished deck will hold, the created ones included. */
  total: number;
  /** Slides actually in the deck right now. */
  placed: number;
  /** Plan positions that failed every attempt, in order. */
  failed: number[];
  /**
   * Which attempt at this deck is current: 0 for the first, +1 per retry.
   *
   * The runner identifies a run by (story, attempt). It must NOT identify one
   * by the failure list, which is the mistake this replaced: `failed` grows
   * while the run is in flight, so the run's own identity changed under it and
   * a second run started alongside the first -- replanning the deck and
   * inserting slides against a stale idea of what was already placed.
   */
  attempt: number;
  /**
   * Where each placed position ended up, as plan position -> slide id.
   *
   * Kept across runs because a RETRY has to put a slide back in its proper
   * place, and by then the slides around it are already in the document. A
   * runner that only remembered the current run would anchor a retried slide
   * to whatever it happened to place last.
   */
  placedIds: Record<number, string>;
  /**
   * The slides the story was created with, which the runner drops once a real
   * one has landed. The editor paints them as a blank, slowly blinking card
   * (editor/components/deck-build-shield) so nothing in them reads as content
   * worth editing. Set by the runner when the plan arrives; before that the
   * editor treats every slide in the deck as one, since nothing real exists.
   */
  placeholderIds: string[];
  start: (job: DeckBuildJob) => void;
  /** The runner has decided which slides are placeholders. */
  notePlaceholders: (ids: string[]) => void;
  /** The deck is known: this many slides are coming. */
  planned: (total: number) => void;
  markPlaced: (count: number) => void;
  notePlaced: (position: number, id: string) => void;
  finish: (
    status: Exclude<DeckBuildStatus, "idle" | "planning" | "building">,
  ) => void;
  stop: () => void;
  /** Re-runs only the positions in `failed`. */
  retry: () => void;
  reset: () => void;
};

export const useDeckBuildStore = create<DeckBuildState>((set, get) => ({
  job: null,
  status: "idle",
  total: 0,
  placed: 0,
  failed: [],
  placedIds: {},
  placeholderIds: [],
  attempt: 0,
  start: (job) =>
    set({
      job,
      status: "planning",
      // Not known until the deck is planned.
      total: 0,
      placed: 0,
      failed: [],
      placedIds: {},
      placeholderIds: [],
      attempt: 0,
    }),
  notePlaceholders: (ids) => set({ placeholderIds: ids }),
  planned: (total) => set({ status: "building", total }),
  markPlaced: (count) => set({ placed: count }),
  notePlaced: (position, id) =>
    set((s) => ({ placedIds: { ...s.placedIds, [position]: id } })),
  finish: (status) => set({ status }),
  // Stop leaves `job` in place: the deck keeps the slides it has, and the
  // banner still has a total to say "8 of 12" with.
  stop: () => set({ status: "stopped" }),
  retry: () => {
    // A retry re-enters "building" with the same job; the runner reads
    // `failed` to know which positions to ask for again. Clearing it here
    // would erase the very list the retry runs on, so the runner clears it
    // when it picks the run up.
    if (!get().job) return;
    set({ status: "building", attempt: get().attempt + 1 });
  },
  reset: () =>
    set({
      job: null,
      status: "idle",
      total: 0,
      placed: 0,
      failed: [],
      placedIds: {},
      placeholderIds: [],
      attempt: 0,
    }),
}));

/** Records a plan position that could not be generated. */
export const noteFailedSlide = (index: number) =>
  useDeckBuildStore.setState((s) =>
    s.failed.includes(index) ? s : { failed: [...s.failed, index] },
  );

/** Clears the failure list at the start of a run. */
export const clearFailedSlides = () =>
  useDeckBuildStore.setState({ failed: [] });

/** Progress for the funnel loader and the editor banner, without re-rendering on every field. */
export const useDeckBuildProgress = () =>
  useDeckBuildStore(
    useShallow((s) => ({
      status: s.status,
      total: s.total,
      placed: s.placed,
      failedCount: s.failed.length,
      storyId: s.job?.storyId ?? null,
      // Every standalone version of a user's shares one story -- the hidden
      // host page -- so the story alone does not say which document is being
      // built (see features/standalone-versions).
      versionId: s.job?.versionId ?? null,
    })),
  );
